/**
 * 根据 MIME 和文件名推断媒体 type，与后端 assets 表 type 枚举一致。
 * 优先按 mimeType 判断；mime 缺失或为 application/octet-stream 时用文件名后缀兜底。
 */

export type MediaType =
  | "image"
  | "document"
  | "audio"
  | "video"
  | "archive"
  | "data"
  | "binary";

function ext(fileName: string): string {
  if (!fileName) return "";
  const i = fileName.lastIndexOf(".");
  if (i === -1 || i === fileName.length - 1) return "";
  return fileName.slice(i + 1).toLowerCase();
}

const DOCUMENT_MIME_PREFIXES = [
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument",
  "application/vnd.ms-excel",
  "application/vnd.ms-powerpoint",
  "text/plain",
  "text/markdown",
] as const;

const DATA_MIME_PREFIXES = [
  "application/json",
  "text/csv",
  "text/xml",
  "application/xml",
  "text/yaml",
  "application/x-yaml",
  "application/yaml",
] as const;

const ARCHIVE_MIME_PREFIXES = [
  "application/zip",
  "application/x-zip-compressed",
  "application/x-rar-compressed",
  "application/x-7z-compressed",
  "application/x-tar",
  "application/gzip",
] as const;

/** 设计软件 MIME（非 image/*）：Photoshop / Illustrator / XD / CorelDRAW 等，归类为 image */
const IMAGE_DESIGN_MIME_PREFIXES = [
  "application/illustrator",
  "application/postscript",
  "application/x-coreldraw",
  "image/x-corelcdr",
] as const;

const DOCUMENT_SUFFIXES = [
  "txt", "md", "markdown", "pdf", "doc", "docx", "xls", "xlsx", "ppt", "pptx",
  "rtf", "odt", "ods", "odp",
];
const DATA_SUFFIXES = ["json", "csv", "xml", "yaml", "yml"];
const ARCHIVE_SUFFIXES = ["zip", "rar", "7z", "tar", "gz", "bz2", "xz"];
/** 设计软件后缀（MIME 常为 octet-stream 时兜底）：psd / ai / eps / xd / cdr 等 */
const IMAGE_DESIGN_SUFFIXES = ["psd", "ai", "eps", "xd", "cdr", "cdt"];

function isGenericMime(mime: string): boolean {
  return !mime || mime === "application/octet-stream";
}

/**
 * 根据 mimeType 和 fileName 返回后端 assets.type 枚举值。
 * 有明确 mimeType 时仅按 MIME 判断；否则按文件名后缀兜底。
 */
export function getMediaTypeForMedia(
  mimeType?: string,
  fileName?: string
): MediaType {
  const mime = (mimeType ?? "").toLowerCase();
  const suffix = ext(fileName ?? "");
  const useSuffixFallback = isGenericMime(mime);

  if (mime.startsWith("image/")) return "image";
  if (mime.startsWith("video/")) return "video";
  if (mime.startsWith("audio/")) return "audio";
  if (IMAGE_DESIGN_MIME_PREFIXES.some((p) => mime.startsWith(p))) return "image";

  if (!useSuffixFallback) {
    if (DOCUMENT_MIME_PREFIXES.some((p) => mime.startsWith(p))) return "document";
    if (DATA_MIME_PREFIXES.some((p) => mime.startsWith(p))) return "data";
    if (ARCHIVE_MIME_PREFIXES.some((p) => mime.startsWith(p))) return "archive";
  }

  if (IMAGE_DESIGN_SUFFIXES.includes(suffix)) return "image";
  if (DOCUMENT_SUFFIXES.includes(suffix)) return "document";
  if (DATA_SUFFIXES.includes(suffix)) return "data";
  if (ARCHIVE_SUFFIXES.includes(suffix)) return "archive";

  return "binary";
}
